//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import moment from 'moment-timezone';

import {
  ConnectionId,
  IBatchCreateConnection,
  UniversalConnectionType,
} from '@apis/universal-posts/connections/universalPostConnectionModel';
import { IDraftPost } from '@apis/universal-posts/draft-posts/universalDraftPostsApiModel';
import { IScheduledPost } from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiModel';
import {
  InstagramUniversalPostType,
  UniversalPostType,
} from '@apis/universal-posts/universalPostsApiModel';
import {
  getSocialNetworkFromPostType,
  getStandardPostDetails,
  isDraftPost,
  isScheduledPost,
  isUniversalPostItem,
} from '@apis/universal-posts/universalPostsUtils';
import Cuid from '@models/Cuid';
import DraftId from '@models/DraftId';
import { IMlsMediaItem, getMediaMetadata } from '@models/IMlsMediaItem';
import {
  InstagramManualStoryClip,
  InstagramStoryGroupClipCustomizationType,
} from '@models/IScheduledInstagramStoryGroup';
import { InstagramPostType } from '@models/InstagramPostType';
import PostableId from '@models/PostableId';
import { mapPostToFormState } from '@universal-scheduling/components/scheduler/post-details-forms/formMappingUtils';
import {
  REELS_TAB_IDEAL_ASPECT_RATIO,
  REELS_TAB_MAX_VIDEO_DURATION_MILLIS,
  REELS_TAB_MIN_VIDEO_DURATION_MILLIS,
} from '@universal-scheduling/components/scheduler/post-details-forms/instagram/InstagramPostDetailsConstants';
import { getDefaultPostDetailsFormState } from '@universal-scheduling/components/scheduler/post-details-forms/postDetailsFormUtils';
import { YOUTUBE_SHORTS_MAX_DURATION_IN_MILLIS } from '@universal-scheduling/components/scheduler/post-details-tabs/postDetailsTabConstants';
import { PINTEREST_RECOMMENDED_ASPECT_RATIO } from '@universal-scheduling/components/scheduler/schedulerConstants';
import { IPostDetailsTabData } from '@universal-scheduling/contexts/scheduler/schedulerSharedStateModels';
import { TSchedulerFormState } from '@universal-scheduling/models/SchedulerPostDetailsModels';
import { schedulerSocialNetworkSorter } from '@universal-scheduling/utils/scheduler/schedulerSocialMediaNetworkUtils';
import { isDefined, isUndefined } from '@utils/filterPredicates';
import uuid from '@utils/uuid';

export const createFormAndTabDataFromPost = async (
  post: IScheduledPost | IDraftPost,
) => {
  if (isUniversalPostItem(post)) {
    const { formState, tabData } = createInitialFormStateAndTabData(
      post.postType,
      await mapPostToFormState(post),
    );

    return {
      formState,
      tabData: {
        ...tabData,
        editDraftId: isDraftPost(post) ? post.id : undefined,
        editPostId: isScheduledPost(post) ? post.id : undefined,
      },
    };
  }
  // only possible if we have updated the backend but didn't add the associated frontend model yet
  throw new Error(`post wasn't a TUniversalPostItem ${post.postType}`);
};

export const getFirstBatchCreateConnection = (
  connections: IBatchCreateConnection[],
): ConnectionId | undefined => {
  return connections.find(
    (connection) =>
      connection.connectionType === UniversalConnectionType.BatchCreate,
  )?.id;
};

export const createPostDetailsTabData = (
  postType: UniversalPostType,
): IPostDetailsTabData => {
  return {
    formId: uuid(),
    socialNetwork: getSocialNetworkFromPostType(postType),
    isActive: true,
    isRead: false,
  };
};

export const createInitialFormStateAndTabData = (
  postType: UniversalPostType,
  initialFormState?: TSchedulerFormState,
) => {
  const tabData = createPostDetailsTabData(postType);
  const { formId } = tabData;
  const formState = initialFormState
    ? { ...initialFormState, formId }
    : { ...getDefaultPostDetailsFormState(postType), formId };

  return {
    formState,
    tabData,
  };
};

export const postDetailsTabSorter = (
  a: IPostDetailsTabData,
  b: IPostDetailsTabData,
): number => {
  const networkA = a.socialNetwork;
  const networkB = b.socialNetwork;

  return schedulerSocialNetworkSorter(networkA, networkB);
};

export const isPostDetailsTabActive = ({
  isActive,
}: IPostDetailsTabData): boolean => {
  return isActive;
};

// TODO PD-247062 do not mark focused tab as unread
export const setAllTabsAsUnread = (
  tabData: IPostDetailsTabData[],
): IPostDetailsTabData[] => {
  return tabData.map((tab) => {
    return {
      ...tab,
      isRead: false,
    };
  });
};

export const getCopyPostState = (
  copiedPost: IDraftPost | IScheduledPost,
  postType: UniversalPostType,
  defaultDate: Date | undefined,
) => {
  const { caption, cuids, scheduledTime, title } =
    getStandardPostDetails(copiedPost);

  const coverFileId = getStandardPostDetails(copiedPost).coverFileId;

  const postDetailsTabData = createPostDetailsTabData(postType);
  const formId = postDetailsTabData.formId;
  const isCopiedScheduledTimeInFuture = moment(scheduledTime).isAfter(
    Date.now(),
  );

  const baseFormState: TSchedulerFormState = {
    ...getDefaultPostDetailsFormState(postType, title),
    formId,
    scheduledTime: isCopiedScheduledTimeInFuture ? scheduledTime : defaultDate,
  };

  const sharedState = {
    selectedMedia: isDefined(cuids) ? cuids : [],
    postDetailsTabData: isDefined(postDetailsTabData)
      ? [postDetailsTabData]
      : [],
  };

  switch (postType) {
    case UniversalPostType.InstagramFeedAutomaticPost:
    case UniversalPostType.InstagramReelsAutomaticPost:
    case UniversalPostType.PinterestOrganicPin:
    case UniversalPostType.YouTubeAutomaticPost:
      return {
        sharedState,
        formState: { ...baseFormState, caption, coverFileId },
      };
    case UniversalPostType.InstagramFeedManualPost:
    case UniversalPostType.InstagramReelsManualPost:
    case UniversalPostType.FacebookPageFeedPost:
    case UniversalPostType.TikTokVideoAutomaticPost:
    case UniversalPostType.TwitterMediaPost:
      return {
        sharedState,
        formState: { ...baseFormState, caption },
      };
    case UniversalPostType.InstagramStoryGroupAutomaticPost:
    case UniversalPostType.InstagramStoryGroupPost:
      return {
        sharedState,
        formState: { ...baseFormState },
      };
  }
};

export const getInitialPostState = (
  postType: UniversalPostType | undefined,
  initialCuid: Cuid | undefined,
  defaultCaption: string | undefined,
  initialPostDateTime: Date | undefined,
) => {
  const postDetailsTabData = isDefined(postType)
    ? createPostDetailsTabData(postType)
    : undefined;
  const scheduledTime = initialPostDateTime;
  const cuids = isDefined(initialCuid) ? [initialCuid] : [];
  const formId = postDetailsTabData?.formId;
  const formState =
    isDefined(postType) && isDefined(formId)
      ? {
          ...getDefaultPostDetailsFormState(postType),
          formId,
          caption: defaultCaption,
          scheduledTime,
        }
      : undefined;

  return {
    sharedState: {
      selectedMedia: cuids,
      postDetailsTabData: isDefined(postDetailsTabData)
        ? [postDetailsTabData]
        : [],
    },
    formState,
  };
};

export const getInstagramPostType = (
  postType: InstagramUniversalPostType,
): InstagramPostType => {
  switch (postType) {
    case UniversalPostType.InstagramFeedAutomaticPost:
    case UniversalPostType.InstagramFeedManualPost:
      return InstagramPostType.Post;
    case UniversalPostType.InstagramReelsAutomaticPost:
    case UniversalPostType.InstagramReelsManualPost:
      return InstagramPostType.Reels;
    case UniversalPostType.InstagramStoryGroupAutomaticPost:
    case UniversalPostType.InstagramStoryGroupPost:
      return InstagramPostType.Story;
  }
};

export const getIsAutomaticPostType = (
  postType: UniversalPostType,
): boolean => {
  switch (postType) {
    case UniversalPostType.InstagramFeedAutomaticPost:
    case UniversalPostType.InstagramReelsAutomaticPost:
    case UniversalPostType.InstagramStoryGroupAutomaticPost:
    case UniversalPostType.FacebookPageFeedPost:
    case UniversalPostType.TikTokVideoAutomaticPost:
    case UniversalPostType.PinterestOrganicPin:
    case UniversalPostType.TwitterMediaPost:
    case UniversalPostType.YouTubeAutomaticPost:
      return true;
    case UniversalPostType.InstagramReelsManualPost:
    case UniversalPostType.InstagramFeedManualPost:
    case UniversalPostType.InstagramStoryGroupPost:
      return false;
  }
};

export const getInstagramUniversalPostType = (
  instagramPostType: InstagramPostType,
  isAutomatic: boolean,
): InstagramUniversalPostType => {
  switch (instagramPostType) {
    case InstagramPostType.Post:
      return isAutomatic
        ? UniversalPostType.InstagramFeedAutomaticPost
        : UniversalPostType.InstagramFeedManualPost;
    case InstagramPostType.Reels:
      return isAutomatic
        ? UniversalPostType.InstagramReelsAutomaticPost
        : UniversalPostType.InstagramReelsManualPost;
    case InstagramPostType.Story:
      return isAutomatic
        ? UniversalPostType.InstagramStoryGroupAutomaticPost
        : UniversalPostType.InstagramStoryGroupPost;
  }
};

export const getStoryClipsInCorrectOrder = (
  mediaItems: IMlsMediaItem[],
  storyClips: InstagramManualStoryClip[],
) => {
  const clips = mediaItems.map((item) => {
    const foundClip = storyClips.find((clip) => clip.cuid === item.cuid);
    if (!foundClip) {
      const clip: InstagramManualStoryClip = {
        cuid: item.cuid,
        customizations: [
          {
            customizationType:
              InstagramStoryGroupClipCustomizationType.SwipeUpUrl,
            value: '',
          },
        ],
      };
      return clip;
    }
    return foundClip;
  });

  // filter out any clips corresponding to removed media
  clips.filter((clip) => {
    return mediaItems.some(({ cuid }) => clip.cuid === cuid);
  });

  return clips;
};

export const getHasStoryClipsOrderChanged = (
  mediaItems: IMlsMediaItem[],
  storyClips: InstagramManualStoryClip[] | undefined,
) => {
  if (isUndefined(storyClips)) {
    return false;
  }

  return (
    mediaItems.some((item, idx) => storyClips[idx]?.cuid !== item.cuid) ||
    storyClips.some((clip, idx) => mediaItems[idx]?.cuid !== clip.cuid)
  );
};

export const getMediaAspectRatio = (
  width: number | undefined,
  height: number | undefined,
): number => {
  return (width ?? 0) / (height ?? 1);
};

export const isMediaDifferentDimensions = (mediaItems: IMlsMediaItem[]) => {
  if (mediaItems.length <= 1) {
    return false;
  }

  const firstMediaMetadata = mediaItems[0] && getMediaMetadata(mediaItems[0]);
  const firstAspectRatio = getMediaAspectRatio(
    firstMediaMetadata?.width,
    firstMediaMetadata?.height,
  );

  return (
    firstMediaMetadata &&
    mediaItems.some((mediaItem) => {
      const mediaMetadata = getMediaMetadata(mediaItem);
      const aspectRatio = getMediaAspectRatio(
        mediaMetadata?.width,
        mediaMetadata?.height,
      );

      return firstAspectRatio !== aspectRatio;
    })
  );
};

const ASPECT_RATIO_LEEWAY = 0.01;
export const isMediaNonRecommendedPinterestDimensions = (
  mediaItems: IMlsMediaItem[],
) =>
  mediaItems.some((mediaItem) => {
    const mediaMetadata = getMediaMetadata(mediaItem);
    const aspectRatio = getMediaAspectRatio(
      mediaMetadata?.width,
      mediaMetadata?.height,
    );

    return (
      aspectRatio < PINTEREST_RECOMMENDED_ASPECT_RATIO - ASPECT_RATIO_LEEWAY ||
      aspectRatio > PINTEREST_RECOMMENDED_ASPECT_RATIO + ASPECT_RATIO_LEEWAY
    );
  });

export const isMediaReelsTabEligible = (mediaItem: IMlsMediaItem) => {
  const mediaMetadata = getMediaMetadata(mediaItem);

  const videoDuration = mediaMetadata?.durationMillis ?? 0;
  const isVideoDurationReelsTabEligible =
    videoDuration <= REELS_TAB_MAX_VIDEO_DURATION_MILLIS &&
    videoDuration >= REELS_TAB_MIN_VIDEO_DURATION_MILLIS;

  const videoAspectRatio = getMediaAspectRatio(
    mediaMetadata?.width,
    mediaMetadata?.height,
  );

  const isVideoAspectRatioReelsTabEligible =
    videoAspectRatio === REELS_TAB_IDEAL_ASPECT_RATIO;

  return isVideoDurationReelsTabEligible && isVideoAspectRatioReelsTabEligible;
};

export const getDraftIdsFromTabs = (
  postDetailsTabData: IPostDetailsTabData[],
): DraftId[] => {
  return postDetailsTabData
    .map(({ editDraftId }) => editDraftId)
    .filter(isDefined);
};

export const getPostableIdsFromTabs = (
  postDetailsTabData: IPostDetailsTabData[],
): PostableId[] => {
  return postDetailsTabData
    .map(({ editPostId }) => editPostId)
    .filter(isDefined);
};

export const getInactiveDraftIdsFromTabs = (
  postDetailsTabData: IPostDetailsTabData[],
): DraftId[] => {
  return getInactivePostIds(
    postDetailsTabData.map(({ editDraftId, isActive }) => ({
      id: editDraftId,
      isActive,
    })),
  );
};

export const getInactivePostIds = <T extends PostableId | DraftId>(
  idData: { id?: T; isActive: boolean }[],
): T[] => {
  return idData
    .filter(({ isActive }) => !isActive)
    .map(({ id }) => id)
    .filter(isDefined);
};

export const isYouTubeShortsVideo = (mediaItem: IMlsMediaItem | undefined) => {
  const mediaMetadata = isDefined(mediaItem)
    ? getMediaMetadata(mediaItem)
    : undefined;
  const durationMillis = mediaMetadata?.durationMillis ?? 0;
  const isHorizontal =
    (mediaMetadata?.width ?? 0) > (mediaMetadata?.height ?? 0);

  return (
    durationMillis <= YOUTUBE_SHORTS_MAX_DURATION_IN_MILLIS && !isHorizontal
  );
};
